import '../App.css';
import './HowToPage.css';

import { buildSeoUrl, seoConfig } from '../utils/seoConfig';

import Header from '../components/Header';
import { useAuth } from '../hooks/useAuth';
import { useSeo } from '../hooks/useSeo';

export default function HowToPage() {
  const { isLoading, isAuthenticated } = useAuth();
  const title = 'Menu Translation Guide - Images, Nutrition, Allergens | Menu AI';
  const description =
    '🌍 Translate menus, see dish images, and check calories/allergens before you order. A fast, traveler-friendly guide to eating confidently.';
  const canonical = buildSeoUrl('/menu-translation');
  const imageAlt = 'Menu AI menu translation walkthrough';

  useSeo({
    title,
    description,
    canonical,
    robots: 'index, follow',
    keywords: 'menu translation, menu translator, menu to images, menu nutrition, allergens, restaurant menu',
    og: {
      title,
      description,
      url: canonical,
      image: seoConfig.defaultImage,
      imageAlt,
      type: 'article',
      siteName: seoConfig.siteName,
      locale: seoConfig.locale,
    },
    twitter: {
      card: 'summary_large_image',
      title,
      description,
      url: canonical,
      image: seoConfig.defaultImage,
      imageAlt,
      site: seoConfig.twitterHandle,
      creator: seoConfig.twitterHandle,
      domain: seoConfig.twitterDomain,
    },
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: title,
      description,
      image: [seoConfig.defaultImage],
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': canonical,
      },
      author: {
        '@type': 'Organization',
        name: seoConfig.siteName,
        url: seoConfig.baseUrl,
      },
      publisher: {
        '@type': 'Organization',
        name: seoConfig.siteName,
        url: seoConfig.baseUrl,
        logo: {
          '@type': 'ImageObject',
          url: `${seoConfig.baseUrl}/favicon.png`,
        },
      },
    },
  });

  return (
    <div className="App">
      <Header isLoading={isLoading} isAuthenticated={isAuthenticated} />

      <main className="main-content">
        <div className="container">
          <div className="how-to-page">
            <div className="how-to-hero-section">
              <h1 className="how-to-hero-title">
                Translate and Transform Any Restaurant Menu: See Images, Nutrition Facts, and Allergen Info Instantly
              </h1>
              <p className="how-to-hero-description">
                A traveler‑friendly guide to reading foreign menus, viewing dish photos, and checking food calories and
                allergens—before you order
              </p>
            </div>

            <div className="article-content">
              <section className="article-section">
                <p className="article-intro">
                  Visiting a new city or country and staring at a menu you can’t read? Or maybe you have allergies and
                  need quick, reliable information before choosing a dish. This page shows you how to use our tool to
                  <strong> translate a menu</strong>, turn it into a <strong>visual menu with images</strong>, and get
                  accurate <strong>menu nutrition</strong> and allergen details—so you can order confidently anywhere.
                </p>
                <p>
                  In a few taps, you can <strong>translate menus</strong>, see realistic photos of dishes, and review
                  ingredients, macros, and allergens. Whether you’re a tourist, a picky eater, or tracking{' '}
                  <strong>food calories</strong>, this is your quick guide to eating smarter.
                </p>
                <img src="/image_compressed.png" alt="showing the app in action" className="demo-image" />
              </section>

              <section className="article-section">
                <h2 className="section-title">Why Menu Translation Helps You</h2>
                <p>
                  A good <strong>menu translator</strong> is more than word‑for‑word conversion—it helps you understand
                  what you’re actually going to eat. With clear descriptions, you avoid surprises, language mix‑ups, and
                  awkward guesswork.
                </p>
                <p>
                  This <strong>menu translation app</strong> exists because travelers want an easy way to scan a foreign
                  menu and make a safe, delicious choice. Allergens need to be aware of what they are eating when they
                  don't speak the language
                </p>

                <div className="use-cases-box">
                  <h3>Common traveler situations:</h3>
                  <ul className="use-cases-list">
                    <li>
                      <strong>Chinese menu translation</strong> when you can’t read characters but want to try local
                      dishes.
                    </li>
                    <li>
                      Downloading an <strong>Italian menu translation PDF</strong> to keep offline while sightseeing.
                    </li>
                    <li>
                      <strong>Japanese food menu translation</strong> to tell ramen styles apart and spot toppings you
                      may avoid.
                    </li>
                  </ul>
                </div>
              </section>

              <section className="article-section">
                <h2 className="section-title">From Text to Pictures: See What You’ll Get</h2>
                <p>
                  It’s easier to decide with a photo. Our <strong>menu to images</strong> feature turns dry text into a
                  <strong> visual food menu</strong> so you can see what each dish typically looks like.
                </p>

                <div className="process-steps">
                  <h3>How it works for you:</h3>
                  <div className="steps-grid">
                    <div className="step-card">
                      <div className="step-number">1</div>
                      <h4>Upload or snap the menu</h4>
                      <p>Take a photo or upload a PDF from your phone. That’s it.</p>
                    </div>
                    <div className="step-card">
                      <div className="step-number">2</div>
                      <h4>Automatic OCR + translation</h4>
                      <p>
                        We read the text and <strong>translate the menu</strong> into your language instantly.
                      </p>
                    </div>
                    <div className="step-card">
                      <div className="step-number">3</div>
                      <h4>Dish images appear</h4>
                      <p>
                        Get realistic pictures so you can compare options quickly—perfect for tourists and visual
                        decision‑makers.
                      </p>
                    </div>
                    <div className="step-card">
                      <div className="step-number">4</div>
                      <h4>Ready to order</h4>
                      <p>
                        With images, translation, and nutrition at a glance, choosing your meal becomes stress‑free.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              <section className="article-section">
                <h2 className="section-title">Check Nutrition and Allergens Before You Eat</h2>
                <p>
                  Have dietary needs or health goals? Our app adds <strong>menu nutrition</strong> and allergen flags so
                  you can make informed choices without the awkward back‑and‑forth.
                </p>

                <div className="features-box">
                  <h3>What you’ll see:</h3>
                  <ul className="features-list">
                    <li>
                      Estimated <strong>food calories</strong> per dish.
                    </li>
                    <li>
                      Clear allergen notices for <strong>gluten, dairy, nuts, shellfish, soy</strong>, and more.
                    </li>
                    <li>
                      <strong>Macro breakdowns</strong> (protein, carbs, fats) for tracking goals.
                    </li>
                    <li>
                      <strong>Per‑portion nutrition</strong> so you know what you’re actually eating.
                    </li>
                  </ul>
                </div>

                <div className="example-dish">
                  <h4>Example:</h4>
                  <div className="dish-example">
                    <h5>Grilled Salmon with Vegetables</h5>
                    <p>~450 calories • 35g protein • Gluten‑free • Contains fish</p>
                  </div>
                </div>
              </section>

              <section className="article-section">
                <h2 className="section-title">Your Simple Flow: Translate, See, Decide</h2>
                <p>
                  You don’t need to be techy. Think of it as a quick <strong>menu translation</strong> plus pictures and
                  nutrition in one place.
                </p>

                <div className="workflow-steps">
                  <ol className="workflow-list">
                    <li>
                      Open the <a href="https://menu-images.com">menu-images.com</a> and upload a menu photo or PDF.
                    </li>
                    <li>
                      Let the app create images and show allergens and <strong>food calories</strong>.
                    </li>
                    <li>
                      Review the <strong>visual menu</strong>, compare dishes, and order with confidence.
                    </li>
                  </ol>
                </div>
              </section>

              <section className="article-section final-thoughts">
                <h2 className="section-title">Eat Confidently—Anywhere</h2>
                <p>
                  Whether you’re a tourist, a student abroad, or someone with allergies, our tool helps you{' '}
                  <strong>translate a menu</strong>, preview dishes with <strong>menu to images</strong>, and check{' '}
                  <strong>menu nutrition</strong> before you order. Less guesswork, more great meals. Try it on your
                  next restaurant visit and enjoy eating like a local—safely and confidently.
                </p>
              </section>
            </div>
          </div>
        </div>
      </main>

      <footer className="footer">
        <p>
          Made with ❤️ by{' '}
          <a href="https://x.com/jordibosch20" target="_blank" rel="noopener noreferrer">
            @jordibosch20
          </a>
        </p>
      </footer>
    </div>
  );
}
